import { Link } from "react-router-dom";
import { dateLocale } from "@/lib/dateTime";
import { DoNotCallBadge } from "@/components/calling/DoNotCallBadge";
import { Mail, Pencil, Phone, Pin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { VisitActions } from "@/components/visits/VisitActions";
import { StatusPill, initialsOf } from "@/components/patterns";
import { WhatsAppIcon } from "@/components/whatsapp/WhatsAppIcon";
import { usePins } from "@/lib/usePins";
import { useToast } from "@/context/ToastContext";
import type { LeadRecord } from "@/types";

const STEPS = [
  { key: "NEW", label: "New" },
  { key: "CONTACTED", label: "Contacted" },
  { key: "FOLLOW_UP", label: "Follow-up" },
  { key: "QUALIFIED", label: "Qualified" },
  { key: "CONVERTED", label: "Converted" },
] as const;

type Step = "CONTACTED" | "FOLLOW_UP" | "QUALIFIED";
/** The stages a click on the bar may move a lead to (the server allows the same; Follow-up can be skipped) */
const NEXT_STEPS: Record<string, Step[]> = { NEW: ["CONTACTED"], CONTACTED: ["FOLLOW_UP", "QUALIFIED"], FOLLOW_UP: ["QUALIFIED"] };

const fmt = (d?: string | null) => (d ? new Date(d).toLocaleDateString(dateLocale(), { day: "2-digit", month: "short" }) : "");

/** Who the lead is, what can be done now, and where it stands on the road to a deal */
export function LeadRecordHeader({
  lead,
  name,
  can,
  onCall,
  onWhatsApp,
  onEmail,
  onEdit,
  onDisqualify,
  onConvert,
  onStep,
  busy,
  untouchedDays = 0,
}: {
  lead: LeadRecord;
  name: string;
  can: { update: boolean; convert: boolean; whatsapp: boolean };
  onCall: () => void;
  onWhatsApp: () => void;
  onEmail: () => void;
  onEdit: () => void;
  onDisqualify: () => void;
  onConvert: () => void;
  onStep: (status: Step) => void;
  busy: boolean;
  /** After this many days without contact a lead is flagged (the super admin's setting); 0 = never */
  untouchedDays?: number;
}) {
  const { pathname } = window.location;
  const pins = usePins();
  const { error } = useToast();
  const status = lead.lead_status;
  const converted = status === "CONVERTED" || !!lead.converted_at;
  const disqualified = status === "DISQUALIFIED";
  const priority = lead.priority || (Number(lead.score) >= 60 ? "HOT" : "WARM");
  const phone = lead.mobile || lead.phone;
  const lastTouch = lead.last_contacted_at || lead.created_on;
  const idleDays = untouchedDays > 0 && lastTouch ? Math.floor((Date.now() - new Date(lastTouch).getTime()) / 86400000) : null;
  const at = STEPS.findIndex((s) => s.key === status);
  const place = [lead.city, lead.state].filter(Boolean).join(", ");
  const sub = [lead.job_title, lead.company_name, place].filter(Boolean);

  return (
    <section aria-label="Lead summary" className="flex flex-col gap-3 rounded-lg border border-border bg-card px-4 py-3.5">
      <div className="flex flex-wrap items-start gap-3">
        <span className="flex size-12 shrink-0 items-center justify-center rounded-full bg-accent text-sm font-bold text-accent-foreground">{initialsOf(name)}</span>
        <div className="flex min-w-[240px] flex-1 flex-col gap-1">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-[19px] font-semibold leading-tight tracking-tight">{name}</h1>
            {/* Hot, Warm and Cold say how keen a lead is; once it is a deal, the stage says everything */}
            {!converted && <StatusPill tone={priority === "HOT" ? "gold" : priority === "COLD" ? "info" : "warning"}>{priority}</StatusPill>}
            <StatusPill tone={converted ? "success" : disqualified ? "danger" : "primary"}>{status === "FOLLOW_UP" ? "Follow-up" : status.charAt(0) + status.slice(1).toLowerCase()}</StatusPill>
            {idleDays != null && idleDays >= untouchedDays && !converted && !disqualified && <StatusPill tone="danger">Not contacted {idleDays} days</StatusPill>}
            <DoNotCallBadge type="LEAD" id={lead.id} flagged={(lead as any).do_not_call} />
          </div>
          <div className="text-[13px] text-muted-foreground">
            {sub.map((p, i) => (
              <span key={i}>
                {i > 0 && " · "}
                {i === 1 ? <strong className="font-medium text-foreground">{p}</strong> : p}
              </span>
            ))}
            {converted && lead.converted_account_id && (
              <>
                {" · "}
                <Link to={`/accounts/${lead.converted_account_id}`} className="text-primary hover:underline">
                  Open account
                </Link>
              </>
            )}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            size="icon-sm"
            aria-pressed={pins.isPinned(pathname)}
            aria-label={pins.isPinned(pathname) ? "Unpin from Home" : "Pin to Home"}
            title={pins.isPinned(pathname) ? "Unpin from Home" : "Pin to Home"}
            onClick={() => pins.toggle(pathname, name).catch((e) => error("Could not change the pin", e?.message || "Try again"))}
          >
            <Pin className={`size-3.5 ${pins.isPinned(pathname) ? "fill-current text-primary" : ""}`} />
          </Button>
          {can.update && (
            <Button variant="outline" size="sm" onClick={onEdit}>
              <Pencil className="mr-1.5 size-3.5" />
              Edit
            </Button>
          )}
          {phone && (
            <Button variant="outline" size="sm" onClick={onCall}>
              <Phone className="mr-1.5 size-3.5" />
              Call
            </Button>
          )}
          {phone && can.whatsapp && (
            <Button variant="outline" size="sm" onClick={onWhatsApp}>
              <WhatsAppIcon className="mr-1.5 size-3.5" />
              WhatsApp
            </Button>
          )}
          {lead.email && (
            <Button variant="outline" size="sm" onClick={onEmail}>
              <Mail className="mr-1.5 size-3.5" />
              Email
            </Button>
          )}
          {!converted && !disqualified && <VisitActions customer={{ type: "LEAD", id: lead.id, label: lead.company_name || name }} />}
          {!converted && !disqualified && can.update && (
            <Button variant="outline" size="sm" className="text-danger hover:text-danger" onClick={onDisqualify} disabled={busy}>
              Disqualify
            </Button>
          )}
          {status === "QUALIFIED" && !converted && can.convert && (
            <Button size="sm" className="bg-sidebar text-white hover:bg-sidebar/90" onClick={onConvert} disabled={busy}>
              <span className="mr-1.5 size-2 rounded-full bg-gold" />
              Convert to deal
            </Button>
          )}
        </div>
      </div>

      {!disqualified && (
        <ol aria-label="Lead stage" className="grid grid-cols-5 gap-[3px]">
          {STEPS.map((s, i) => {
            const state = i < at || (converted && i <= at) ? "done" : i === at ? "now" : "later";
            const next = state === "later" && !converted && can.update && !!NEXT_STEPS[status]?.includes(s.key as Step);
            const date = s.key === "NEW" ? fmt(lead.created_on) : s.key === "CONTACTED" ? fmt(lead.last_contacted_at) : s.key === "FOLLOW_UP" ? fmt(lead.next_follow_up_at) : s.key === "CONVERTED" ? fmt(lead.converted_at) : "";
            return (
              <li key={s.key} className="min-w-0">
                <button
                  type="button"
                  disabled={!next || busy}
                  onClick={() => next && onStep(s.key as Step)}
                  title={next ? `Mark as ${s.label.toLowerCase()}` : undefined}
                  className="flex w-full flex-col gap-1.5 text-left disabled:cursor-default"
                >
                  <span className={`h-1.5 rounded-sm ${state === "done" ? "bg-sidebar" : state === "now" ? "bg-primary" : next ? "bg-primary/30" : "bg-border"}`} />
                  <span className={`truncate text-[11.5px] ${state === "later" ? "text-muted-foreground" : "font-medium text-foreground"} ${next ? "text-primary" : ""}`}>
                    {s.label}
                    {date && state !== "later" ? ` · ${date}` : ""}
                  </span>
                </button>
              </li>
            );
          })}
        </ol>
      )}
      {disqualified && (
        <p className="text-[13px] text-danger">
          Disqualified{lead.disqualify_reason ? `: ${lead.disqualify_reason}` : ""}
          {lead.disqualify_notes ? `. ${lead.disqualify_notes}` : ""}
        </p>
      )}
    </section>
  );
}
